import type { Ref } from 'vue'
import type { EventDateType } from '~/composables/types/event'
import {
  useDateRequestFormStorage,
  type DateRequestFormData,
} from '~/composables/useDateRequestFormStorage'

export interface DateRequestFormModel {
  email: string
  phone: string
  name: string
  surname: string
  numberOfTickets: number | null
  companyName: string
  city: string
  dateType: EventDateType | null
  dates: string[]
  additionalInfo?: string
}

const createEmptyFormModel = (): DateRequestFormModel => ({
  email: '',
  phone: '',
  name: '',
  surname: '',
  numberOfTickets: null,
  companyName: '',
  city: '',
  dateType: null,
  dates: [],
  additionalInfo: '',
})

const normalizeDateType = (
  value: DateRequestFormData['dateType']
): EventDateType | null =>
  value === 'PRIVATE' || value === 'PUBLIC' ? value : null

/**
 * Owns the date-request form ref and a single localStorage hydration path.
 * Returns the form together with `hasRestoredTicketCount`, which indicates
 * that a non-empty ticket count was hydrated from storage (used by callers
 * to decide whether the user has already committed a ticket count).
 */
export const useDateRequestFormState = (): {
  form: Ref<DateRequestFormModel>
  hasRestoredTicketCount: Ref<boolean>
} => {
  const form = ref<DateRequestFormModel>(createEmptyFormModel())
  const hasRestoredTicketCount = ref(false)

  const { savedFormData, loaded } = useDateRequestFormStorage()

  const applySavedData = (data: DateRequestFormData) => {
    form.value = {
      ...createEmptyFormModel(),
      ...data,
      dateType: normalizeDateType(data.dateType),
      dates: [...(data.dates ?? [])],
    }
    const restored = data.numberOfTickets
    if (restored !== null && restored !== undefined && restored !== 0) {
      hasRestoredTicketCount.value = true
    }
  }

  // Single hydration: fires once when localStorage finishes loading, and
  // again only if `savedFormData` is reassigned (no deep watch).
  watch(
    [loaded, savedFormData],
    ([isLoaded, data]) => {
      if (!isLoaded || !data) {
        return
      }
      applySavedData(data)
    },
    { immediate: true }
  )

  return {
    form,
    hasRestoredTicketCount,
  }
}
